import React, { useState, useEffect } from 'react';
import { View, Text, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createConversation, updateConversation } from '@/data/connect';
import Toast from 'react-native-toast-message'
import { choosePhoto, getFile, saveFile } from '@/utils/FileManager';
import Avatar from '@/components/People/Avatar';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import * as Crypto from 'expo-crypto';
import InputSection from '@/components/General/inputs/InputSection';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import ModalWrapper from '@/components/General/ModalWrapper';
import Unicon from '@/components/General/Unicon';

const EMPTY_DRAFT = {};

export default function AddConversationModal() {
    const { id } = useLocalSearchParams();
    const storeKey = id || "new"

    const [ newAvatar, setNewAvatar ] = useState({})

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const conversationDetails = useEditScreenStore(s => s.AddConversationModal?.[storeKey]) || EMPTY_DRAFT

    const queryClient = useQueryClient()
    const router = useRouter()
    const pathname = usePathname()

    const { data: loggedInUser } = Query.people.loggedInUser()
    const { data: conversationToEdit } = Query.conversations.byId({id, options: {enabled: !!id}})
    const eligiblePeopleQuery = Query.people.withConversationAccess({orgID: loggedInUser?.organizationID, options: {enabled: !!loggedInUser?.organizationID}})

    const createMutation = useMutation({
        mutationFn: createConversation,
        onSuccess: async (data) => {
            if(data?.existingID){
                router.replace(`/connect/conversations/${data.existingID}`);
                return;
            }

            if(!data?.id){
                Toast.show({type: 'error', text2: 'Something went wrong creating that conversation'});
                return;
            }

            removeEditScreenData("AddConversationModal", storeKey)
            queryClient.invalidateQueries({queryKey: QueryKeys.conversations.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.conversations.byUserId(loggedInUser?.id)});

            if(data.avatarPath && newAvatar?.uri){
                saveFile(newAvatar?.uri, data.avatarPath);
            }

            router.replace(`/connect/conversations/${data?.id}`);
        },
        onError: (err) => {
            console.debug("Coversation creation failed: ", err)
        },
    })


    const updateMutation = useMutation({
        mutationFn: updateConversation,
        onSuccess: async (data) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.conversations.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.conversations.byUserId(loggedInUser?.id)});

            if(data.avatarPath && newAvatar?.uri){
                saveFile(newAvatar?.uri, data.avatarPath);
            }

            router.replace(`/connect/conversations/${data?.id}`);
        },
        onError: (err) => {
            console.debug("Coversation creation failed: ", err)
        },
    })

    /*** Effects ***/
    useEffect(() => {
        if(conversationToEdit){
            setEditScreenData("AddConversationModal", storeKey, {
                ...conversationToEdit, 
                name: conversationToEdit.name == "*NONE*" ? null : conversationToEdit.name,
                People: conversationToEdit.membersByGroup ? [] : conversationToEdit.People?.filter(p => p?.id != loggedInUser?.id),
                Roles: conversationToEdit.membersByGroup ? (conversationToEdit.Roles || []) : [],
            })

            setImage();
        }
    }, [conversationToEdit]);


    const setConversationDetails = (details) => {
        setEditScreenData("AddConversationModal", storeKey, details)
    }


    const setImage = async () => {
        const {uri, size, exists} = await getFile(conversationToEdit?.avatarPath, true, true);

        if(exists && size > 0){
            setNewAvatar({
                uri: uri,
                name: id + ".jpg",
                type: 'image/jpg'
            })
        }
    }


    const avatar = <Avatar key={newAvatar?.uri} borderGap size={50} profile={{uri: newAvatar?.uri}} directory="conversations/" onPress={async () => {
        const assets = await choosePhoto({noVideos: true});
        if(assets && assets.length > 0) {
            setConversationDetails({
                avatarPath: `${loggedInUser?.organizationID}/conversations/${conversationDetails.id + "_" + new Date().getTime() + ".jpg"}`
            })

            setNewAvatar({
                uri: assets[0].uri,
                name: conversationDetails.id + "_" + new Date().getTime() + ".jpg",
                type: 'image/jpg'
            })
        }
    }}/>

    const isRoleBased = conversationDetails.membersByGroup === true;
    //Can't switch mode when editing an existing conversation
    const canToggleMode = !id;

    
    return (
        <ModalWrapper
            title={id ? "Update Conversation" : "New Conversation"}
            onConfirm={() => {
                if(isRoleBased){
                    if(!conversationDetails.Roles || conversationDetails.Roles.length == 0){
                        Toast.show({
                            type: 'error',
                            text2: `Add at least one role to the conversation!`,
                            bottomOffset:"50"
                        });
                        return true;
                    }
                }
                else{
                    if(!conversationDetails.People || Object.values(conversationDetails.People).length == 0){
                        Toast.show({
                            type: 'error',
                            text2: `You can't converse with yourself! (Well, at least not on here). Add some members!`,
                            bottomOffset:"50"
                        });
                        return true;
                    }

                    let peopleToAdd = conversationDetails.People
                    peopleToAdd.push(loggedInUser)
                }

                if(id){
                    const avatarPath = newAvatar?.uri ? `${loggedInUser?.organizationID}/conversations/${conversationDetails.id + "_" + new Date().getTime() + ".jpg"}` : conversationDetails.avatarPath;
                    updateMutation.mutate({details: {...conversationDetails, avatarPath}});
                }
                else{
                    const newId = Crypto.randomUUID();
                    const avatarPath = newAvatar?.uri ? `${loggedInUser?.organizationID}/conversations/${newId + "_" + new Date().getTime() + ".jpg"}` : undefined;
                    createMutation.mutate({details: {...conversationDetails, id: newId, avatarPath, createdByID: loggedInUser?.id}});
                }
                return true;
            }} 
        >

            <View>
                <InputSection title="Conversation Info" description="Information for this conversation">
                    <View style={styles.avatarContainer}>
                        {avatar}
                    </View>

                    <Input
                        title="Conversation Name (Optional)"
                        value={conversationDetails.name}
                        onUpdate={(new_value) => {
                            setConversationDetails({
                                name: new_value
                            })
                        }}
                    />

                    <View style={styles.membersSection}>
                        <View style={styles.membersHeader}>
                            <Text style={styles.membersLabel}>{isRoleBased ? "Roles" : "Members"}</Text>
                            {canToggleMode && (
                                <View style={styles.modeToggle}>
                                    <Pressable
                                        testID="mode-people"
                                        style={[styles.modeButton, !isRoleBased && styles.modeButtonActive]}
                                        onPress={() => setConversationDetails({membersByGroup: false, People: [], Roles: []})}
                                    >
                                        <Text style={[styles.modeButtonText, !isRoleBased && styles.modeButtonTextActive]}>People</Text>
                                    </Pressable>
                                    <Pressable
                                        testID="mode-roles"
                                        style={[styles.modeButton, isRoleBased && styles.modeButtonActive]}
                                        onPress={() => setConversationDetails({membersByGroup: true, People: [], Roles: []})}
                                    >
                                        <Text style={[styles.modeButtonText, isRoleBased && styles.modeButtonTextActive]}>Roles</Text>
                                    </Pressable>
                                </View>
                            )}
                            {isRoleBased ? (
                                <SheetSelector
                                    key="roles-selector"
                                    multiple 
                                    type="roles"
                                    title="Add Roles" 
                                    fromScreen={pathname} 
                                    fromStoreKey={storeKey} 
                                    value={conversationDetails.Roles || []} 
                                    onChange={(selected_items) => {
                                        setConversationDetails({Roles: selected_items})
                                    }}
                                >
                                    <View style={styles.addButton}>
                                        <Unicon name="people-outline" size={18} colorVariant="onPrimary"/>
                                        <Text style={styles.addButtonText}>Add</Text>
                                    </View>
                                </SheetSelector>
                            ) : (
                                <SheetSelector
                                    key="people-selector"
                                    multiple 
                                    type="people" 
                                    excludeIds={[loggedInUser?.id]} 
                                    title="Add Members" 
                                    fromScreen={pathname} 
                                    fromStoreKey={storeKey} 
                                    value={conversationDetails.People || []} 
                                    queryResult={eligiblePeopleQuery}
                                    onChange={(selected_items) => {
                                        setConversationDetails({People: selected_items})
                                    }}
                                >
                                    <View style={styles.addButton}>
                                        <Unicon name="person-add" size={18} colorVariant="onPrimary"/>
                                        <Text style={styles.addButtonText}>Add</Text>
                                    </View>
                                </SheetSelector>
                            )}
                        </View>

                        {isRoleBased ? (
                            conversationDetails.Roles?.length > 0 ? (
                                <View style={styles.memberList}>
                                    {conversationDetails.Roles.map((role) => (
                                        <View key={role.id} style={styles.memberRow}>
                                            <View style={styles.roleIconBubble}>
                                                <Unicon name="people-outline" size={16} colorVariant="primary"/>
                                            </View>
                                            <Text style={styles.memberName} numberOfLines={1}>
                                                {role.name}
                                            </Text>
                                            <Pressable 
                                                testID={`remove-role-${role.id}`}
                                                hitSlop={10} 
                                                onPress={() => {
                                                    setConversationDetails({
                                                        Roles: conversationDetails.Roles.filter(r => r.id !== role.id)
                                                    })
                                                }}
                                            >
                                                <Unicon name="close-circle" size={22} colorVariant="error"/>
                                            </Pressable>
                                        </View>
                                    ))}
                                </View>
                            ) : (
                                <Text style={styles.emptyText}>{'No roles added yet. Tap "Add" to select roles.'}</Text>
                            )
                        ) : (
                            conversationDetails.People?.length > 0 ? (
                                <View style={styles.memberList}>
                                    {conversationDetails.People.map((person) => (
                                        <View key={person.id} style={styles.memberRow}>
                                            <Avatar size={32} profile={person} disableBorder onPress={() => {}}/>
                                            <Text style={styles.memberName} numberOfLines={1}>
                                                {person.firstName} {person.lastName}
                                            </Text>
                                            <Pressable 
                                                testID={`remove-member-${person.id}`}
                                                hitSlop={10} 
                                                onPress={() => {
                                                    setConversationDetails({
                                                        People: conversationDetails.People.filter(p => p.id !== person.id)
                                                    })
                                                }}
                                            >
                                                <Unicon name="close-circle" size={22} colorVariant="error"/>
                                            </Pressable>
                                        </View>
                                    ))}
                                </View>
                            ) : (
                                <Text style={styles.emptyText}>{'No members added yet. Tap "Add" to select people. Only showing Reach users that have access to Conversations.'}</Text>
                            )
                        )}
                    </View>

                </InputSection>
            </View>
        
        </ModalWrapper> 
    );
}

const styles = StyleSheet.create(({colors, fonts, spacing}) => ({
    avatarContainer: {
        position:'absolute',
        top:15,
        right:20,
        alignItems:'center',
        justifyContent:'center'
    },
    membersSection: {
        marginVertical: spacing.sm,
    },
    membersHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: spacing.xs,
    },
    membersLabel: {
        fontSize: 13,
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    addButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: colors.primary,
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 10,
    },
    addButtonText: {
        color: colors.onPrimary,
        fontSize: 14,
        fontFamily: fonts.body,
    },
    memberList: {
        gap: 6,
    },
    memberRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 6,
        paddingHorizontal: 4,
        borderRadius: 10,
        backgroundColor: colors.hover,
    },
    memberName: {
        flex: 1,
        fontSize: 15,
        fontFamily: fonts.body,
        color: colors.text,
    },
    emptyText: {
        fontSize: 14,
        color: colors.textTertiary,
        fontStyle: 'italic',
        paddingVertical: 12,
    },
    modeToggle: {
        flexDirection: 'row',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.border,
        overflow: 'hidden',
    },
    modeButton: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        backgroundColor: colors.surface,
    },
    modeButtonActive: {
        backgroundColor: colors.primary,
    },
    modeButtonText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    modeButtonTextActive: {
        color: colors.onPrimary,
        fontFamily: fonts.bold,
    },
    roleIconBubble: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
}))